import { useEffect, useRef, useState } from "react";

/*
 * Modal de reagendamento manual de uma notificacao (FE-7 — Tela 7 §10.5).
 *
 * Espelha a estrutura do `ConfirmacaoConciliacao`/`ModalErro`: `role="dialog"` +
 * `aria-modal`, rotulado por `aria-labelledby`, foco movido ao abrir e Escape para
 * fechar. O reagendamento e uma acao de EFEITO EXTERNO (reenvia a notificacao) e
 * exige uma JUSTIFICATIVA com >= 10 caracteres (apos `.trim()`) — o mesmo gate do
 * backend (422 abaixo disso). O Confirmar fica DESABILITADO enquanto a
 * justificativa nao atinge o minimo, e troca o rotulo para "Processando…" enquanto
 * a mutacao esta em voo.
 *
 * O estado da textarea e LOCAL do modal; `aoConfirmar(justificativa)` e injetado
 * pelo orquestrador (`HistoricoAlertas`), que conhece o hook de mutacao.
 */

const MINIMO_JUSTIFICATIVA = 10;

interface ModalReagendarProps {
  /** Dispara a mutacao real (POST) com a justificativa informada. */
  aoConfirmar: (justificativa: string) => void;
  /** Fecha o modal sem efeito. */
  aoCancelar: () => void;
  /** Mutacao em voo: desabilita os botoes e troca o rotulo do Confirmar. */
  processando: boolean;
}

export function ModalReagendar({
  aoConfirmar,
  aoCancelar,
  processando,
}: ModalReagendarProps): JSX.Element {
  const [justificativa, setJustificativa] = useState("");
  const cancelarRef = useRef<HTMLButtonElement>(null);

  // Move o foco para o primeiro botao ao abrir (acessibilidade de modal).
  useEffect(() => {
    cancelarRef.current?.focus();
  }, []);

  const justificativaValida =
    justificativa.trim().length >= MINIMO_JUSTIFICATIVA;
  const confirmarDesabilitado = !justificativaValida || processando;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-modal-reagendar"
      onKeyDown={(evento) => {
        if (evento.key === "Escape" && !processando) {
          aoCancelar();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-off-black/50 p-4"
    >
      <div className="flex w-full max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
        <h2
          id="titulo-modal-reagendar"
          className="font-titulo text-xl text-off-black"
        >
          Reagendar notificação
        </h2>

        <p>Esta ação reenviará a notificação.</p>

        <label className="flex flex-col gap-1">
          <span className="font-subtitulo">
            Justificativa (mínimo 10 caracteres)
          </span>
          <textarea
            value={justificativa}
            onChange={(evento) => setJustificativa(evento.target.value)}
            disabled={processando}
            rows={4}
            className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black disabled:opacity-60"
          />
        </label>

        <div className="flex justify-end gap-3">
          <button
            ref={cancelarRef}
            type="button"
            onClick={aoCancelar}
            disabled={processando}
            className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => aoConfirmar(justificativa)}
            disabled={confirmarDesabilitado}
            className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
          >
            {processando ? "Processando…" : "Confirmar reagendamento"}
          </button>
        </div>
      </div>
    </div>
  );
}
